/**
 * The wire types this task actually reads, transcribed from `openapi.yaml`.
 *
 * Only what WP-K1 consumes: the session body, the error object, and the shapes the
 * nine shared states are typed against. Transcribing the whole 6 000-line contract
 * here would create a second copy of it to keep in step; each later screen brings
 * the slice it needs.
 *
 * ── About `WorkforcePermission` ─────────────────────────────────────────────
 *
 * The seventeen codes are listed because they are a **vocabulary**, exactly as
 * `src/lib/permissions.ts` says of its own copy: "Nothing here decides anything
 * about a request. Deciding is `src/lib/scope.ts`." The same sentence is true on
 * this side of the wire, and more sharply: the browser decides nothing at all.
 *
 * What is deliberately **not** here is `DEFAULT_GRANTS`. The client never maps a
 * role to a permission set — that mapping lives on the server, is modified by
 * `wp_permission_grants`, and arrives already resolved in
 * `SessionUser.permissions`. A copy of the role table in the browser would be a
 * second authorisation path that drifts the first time a grant row is written.
 */

export const WORKFORCE_PERMISSIONS = [
  'workforce.view_self',
  'workforce.view_department',
  'workforce.view_workspace',
  'workforce.manage_shifts',
  'workforce.manage_policies',
  'workforce.manage_devices',
  'workforce.view_apps',
  'workforce.view_websites',
  'workforce.view_file_events',
  'workforce.view_usb_events',
  'workforce.view_screenshots',
  'workforce.export_reports',
  'workforce.manage_alerts',
  'workforce.resolve_incidents',
  'workforce.view_audit_logs',
  'workforce.manage_retention',
  'workforce.use_ai_analytics',
] as const;

export type WorkforcePermission = (typeof WORKFORCE_PERMISSIONS)[number];

/**
 * The recruiting (Phase 1 ATS) permission vocabulary — the `recruiting.*` codes of
 * `openapi.yaml`'s `x-wp-permissions`, held apart from `WORKFORCE_PERMISSIONS` on
 * purpose.
 *
 * They are a **second vocabulary**, not an extension of the first: the recruiting
 * module is a distinct surface (spec 01 §3's core sixteen are unchanged), and keeping
 * the two lists separate means a test that counts the sixteen core menu items, or one
 * that says "hold every workforce permission", still means exactly what it did before
 * the recruiting screens landed. The same sentence the workforce list carries is true
 * here and more sharply: nothing on this side of the wire decides anything — the
 * server authorises every recruiting call, and this list is a spelling aid so a menu
 * requirement or a route guard cannot name a code that does not exist.
 */
export const HIRING_PERMISSIONS = [
  'recruiting.view_jobs',
  'recruiting.manage_jobs',
  'recruiting.view_candidates',
  'recruiting.manage_candidates',
  'recruiting.view_candidate_documents',
  'recruiting.submit_feedback',
  'recruiting.manage_offers',
  'recruiting.hire',
] as const;

export type HiringPermission = (typeof HIRING_PERMISSIONS)[number];

/**
 * The HR / people-ops (Phase 3a) permission vocabulary — the `hr.*` codes of
 * `openapi.yaml`'s `x-wp-permissions` for the employee-document, contract and
 * onboarding surfaces, held apart from the workforce and recruiting lists for the same
 * reason those two are held apart: a **third vocabulary**, not an extension of either.
 *
 * These endpoints authorise on a **hybrid** of a `hr.*` KIND gate and a
 * `workforce.view_*` WHOSE gate; only the KIND half is spelled here, because the menu
 * and the route guard name the KIND code and the server resolves the WHOSE half over
 * the employee tree on every call. The same sentence the other two lists carry is true
 * here and more sharply: nothing on this side of the wire decides anything — this list
 * is a spelling aid so a menu requirement or a route guard cannot name a code that does
 * not exist. WP-W6's two surfaces and WP-W7's leave surface are listed; later HR tasks
 * (reviews) bring their own.
 *
 * `hr.view_leave`, `hr.request_leave` and `hr.approve_leave` are the WP-W7 leave codes
 * (`openapi.yaml`'s `hr.manage_leave_types` gates a leave-type admin screen no task has
 * built yet, so it is not spelled here — the rule the rest of this list already keeps).
 * `hr.request_leave` and `hr.view_leave` are both on the **self-service floor**:
 * `src/lib/hiring-permissions.ts` gives every role both, employee included, before any
 * grant row — which is why the leave nav item needs no role check to show for everyone,
 * and why an employee reads the type catalogue, their own balances and their own request
 * history by default. Reading is still `self`-scoped by the employee tree, so the pair
 * buys nobody a colleague's rows. The two are separable all the same — an explicit
 * `deny` grant on `hr.view_leave` leaves a session that can file but not read back — so
 * the leave screen's regions stay independent rather than papering the case over.
 *
 * `hr.view_reviews`, `hr.manage_review_cycles`, `hr.submit_review` and
 * `hr.view_own_review` are the WP-W8 performance codes. `hr.view_own_review` — and only
 * that one — is on the self-service floor (`src/lib/hiring-permissions.ts`'s
 * `SELF_SERVICE_FLOOR`), which is why the performance nav item needs no role check
 * either: every employee reaches "My review" through it, even one nobody has ever
 * granted `hr.submit_review` to. Submitting a self- **or** a manager/peer-review runs
 * through the SAME code, `hr.submit_review` — `performance-service.ts`'s
 * `resolveReviewSubject` is where the two diverge, not a second permission.
 */
export const HR_PERMISSIONS = [
  'hr.view_employee_hr',
  'hr.view_employee_documents',
  'hr.manage_employee_documents',
  'hr.manage_onboarding',
  'hr.view_leave',
  'hr.request_leave',
  'hr.approve_leave',
  'hr.view_reviews',
  'hr.manage_review_cycles',
  'hr.submit_review',
  'hr.view_own_review',
] as const;

export type HrPermission = (typeof HR_PERMISSIONS)[number];

/**
 * The payroll (Task 16, Phase 8a) permission vocabulary — the `payroll.*` codes of
 * `src/lib/hiring-permissions.ts`'s `PAYROLL_PERMISSIONS`, held apart from the other
 * three lists for the same reason each of those is held apart: a **fourth
 * vocabulary**, not an extension of any of them.
 *
 * `payroll.view_own_payslip` is the self-service floor every role holds by default
 * (`DEFAULT_HIRING_GRANTS`'s `SELF_SERVICE_FLOOR`) — the payroll nav item needs no
 * role check because of it, exactly as `hr.request_leave` and `hr.view_own_review`
 * work for their own screens. The other six are granted per person:
 * `payroll.view_payroll` (read runs/periods and a run's items, company-wide once
 * granted — ruling P1), `payroll.run_payroll` (create a period
 * or a run, calculate), `payroll.approve_payroll` (the manager step of the two-step
 * approval), `payroll.ceo_approve` (the CEO step — the two approvers must differ,
 * enforced server-side and at the database), and `payroll.manage_templates` /
 * `payroll.manage_regulations` (the salary-component template and the formula
 * version, both company configuration). Nothing on this side of the wire decides
 * anything — this list is a spelling aid so a menu requirement or a route guard
 * cannot name a code that does not exist.
 */
export const PAYROLL_PERMISSIONS = [
  'payroll.view_own_payslip',
  'payroll.view_payroll',
  'payroll.run_payroll',
  'payroll.approve_payroll',
  'payroll.ceo_approve',
  'payroll.manage_templates',
  'payroll.manage_regulations',
] as const;

export type PayrollPermission = (typeof PAYROLL_PERMISSIONS)[number];

/**
 * Every permission code `/auth/me` may carry in `SessionUser.permissions`, and the
 * type the menu projection and the route guard compare against. The union of the
 * four vocabularies rather than one flat list, so each keeps its own identity while a
 * single effective set can hold codes from all of them.
 */
export type DashboardPermission =
  WorkforcePermission | HiringPermission | HrPermission | PayrollPermission;

/** `ScopeLevel` of `openapi.yaml`. Three levels and no "none" — hence the null. */
export type ScopeLevel = 'self' | 'department' | 'company';

/** `CompanyMembership` of `openapi.yaml`. */
export interface CompanyMembership {
  readonly company_id: string;
  readonly name: string;
  /** A disabled company is still listed, greyed rather than silently dropped. */
  readonly enabled: boolean;
}

/**
 * `SessionUser` of `openapi.yaml` — the body of both `POST /auth/login` and
 * `GET /auth/me`, rendered by one server-side function so the two cannot disagree
 * about the shape.
 *
 * `permissions` is the **effective** set: the role floor, plus every live `allow`
 * row expanded down the view ladder, minus every live `deny`. Task 15's fix. It is
 * the single input to the menu projection.
 */
export interface SessionUser {
  readonly employee_id: string;
  readonly active_company_id: string | null;
  readonly companies: readonly CompanyMembership[];
  readonly company_selection_required: boolean;
  readonly email: string | null;
  readonly display_name: string | null;
  readonly role: string | null;
  readonly department_id: string | null;
  readonly timezone: string;
  readonly permissions: readonly DashboardPermission[];
  readonly scope_level: ScopeLevel | null;
  /** The instance kill switch. `/auth/me` keeps answering when it is off (§4). */
  readonly service_enabled: boolean;
  /** `HMAC(csrfKey, sessionToken)`. Held in memory only — never `localStorage`. */
  readonly csrf_token: string;
}

/** The error object of `API_CONTRACT.md` §5. All four fields are always present. */
export interface WorkforceErrorBody {
  readonly error: {
    readonly code: string;
    readonly message: string;
    readonly details: Readonly<Record<string, unknown>>;
    readonly correlation_id: string;
  };
}

/* ─────────────────── the shapes the nine shared states are typed against ─── */

/** `DeviceLiveness` of `openapi.yaml` — computed from `last_heartbeat_at`, never stored. */
export type DeviceLiveness = 'online' | 'stale' | 'offline';

/** `AttendanceDay.completeness` of `openapi.yaml`. */
export interface Completeness {
  readonly score: number;
  readonly missing_agent_data: boolean;
  readonly unsynced_data: boolean;
  readonly device_count?: number;
}

/** `AchauSyncState.last_status` of `openapi.yaml`. */
export type SyncStatus = 'never' | 'running' | 'success' | 'failed' | 'partial';

/* ══════════════════ WP-K2 (Task 42) — My Activity and Time Tracking ═════════
 *
 * Transcribed from `openapi.yaml`, and only the members these two screens read.
 * The module comment above says why the whole contract is not copied here: each
 * screen brings the slice it needs, so there is never a second full copy of a
 * 10 000-line document to keep in step.
 *
 * Optional members are optional **because the contract's `required` list says so**,
 * not because they seemed unlikely. `exactOptionalPropertyTypes` is on, so an
 * absent field and a `null` one are different types here exactly as they are
 * different facts on the wire — and `PRODUCT.md` §4 exists because collapsing them
 * is how an absence becomes a zero.
 */

/** `CursorPage` of `openapi.yaml`. */
export interface CursorPage {
  readonly next_cursor: string | null;
  readonly has_more: boolean;
}

/** `OffsetPage` of `openapi.yaml`. */
export interface OffsetPage {
  readonly offset: number;
  readonly limit: number;
  readonly total: number;
}

export interface OffsetListEnvelope<T> {
  readonly data: readonly T[];
  readonly page: OffsetPage;
}

export interface CursorListEnvelope<T> {
  readonly data: readonly T[];
  readonly page: CursorPage;
}

/* ───────────────────────────────────────────────── attendance (Tasks 26–28) ── */

/**
 * `AttendanceDay` — the slice `/my-activity`'s card reads of one grid cell.
 *
 * A **narrower view of the same wire object** as `AttendanceGridDay` further down,
 * which is the full shape `renderAttendanceDay` sends. Narrow because a self-view
 * needs no shift resolution and no provenance block; corrected by Task 43 in three
 * places where the narrow version was not merely narrow but wrong:
 *
 *  - **every minute field is `number | null`.** The server sends `null` for a day
 *    with no session row (`durationsOf`) and for a metric it declined to publish
 *    (`calc.ts`'s `withheld`). Typed as `number | undefined`, a `null` reached
 *    `formatDurationMinutes`, which clamps a non-finite input to zero — so a
 *    withheld overtime figure rendered as **"0 ph"** on the employee's own screen.
 *    That is `docs/DATA_MODEL.md` D-22's failure exactly: an absence reported as a
 *    measured zero.
 *  - **`withheld` is here**, because a `null` without its reason is a number nobody
 *    can account for.
 *  - **`adjustments_applied` is gone.** `openapi.yaml` declared it and
 *    `API_CONTRACT.md` §8.3 described it; the string appears nowhere in `src/` and
 *    `renderAttendanceDay` does not produce it, so the branch that read it could
 *    never run. QA-B-026 removed it from both documents, so this file is no longer
 *    the only place that says so. See `web/shapes/attendance.md` §4 item 4.
 */
export interface AttendanceDay {
  readonly employee_id: string;
  readonly date: string;
  readonly completeness: Completeness;
  readonly attendance_status?: AttendanceStatus;
  readonly reason?: AttendanceReason | null;
  readonly withheld?: WithheldMetrics;
  readonly shift_id?: string | null;
  readonly first_activity_at?: string | null;
  readonly last_activity_at?: string | null;
  readonly online_minutes?: number | null;
  readonly active_minutes?: number | null;
  readonly idle_minutes?: number | null;
  readonly break_minutes?: number | null;
  readonly approved_offline_minutes?: number | null;
  readonly overtime_minutes?: number | null;
  readonly late_minutes?: number | null;
  readonly early_leave_minutes?: number | null;
  readonly missing_minutes?: number | null;
}

/**
 * What `GET /attendance` actually returns, which is **not** an `OffsetListEnvelope`.
 *
 * `total`, `offset` and `limit` sit at the top level with a `range` beside them, not
 * under a `page` object — see `AttendanceGridResponse` below, which is the same
 * envelope typed over the full cell. This alias existed as
 * `OffsetListEnvelope<AttendanceDay>`, which declared a `page` that is `undefined` at
 * runtime; nothing read it, so it never threw, and a type that describes a response
 * the server cannot send is a trap for whoever writes the next reader.
 */
export interface AttendanceListResponse {
  readonly total?: number;
  readonly offset?: number;
  readonly limit?: number;
  readonly range?: {
    readonly date_from: string;
    readonly date_to: string;
    readonly timezone: string;
  };
  readonly data: readonly AttendanceDay[];
}

/** `ExplanationReasonCode` — the twelve values of the `reason_code` CHECK. */
export const EXPLANATION_REASON_CODES = [
  'meeting',
  'phone_call',
  'reading_document',
  'paperwork',
  'client_visit',
  'break',
  'offsite_work',
  'agent_down',
  'device_issue',
  'power_outage',
  'approved_leave',
  'other',
] as const;

export type ExplanationReasonCode = (typeof EXPLANATION_REASON_CODES)[number];

/** `ApprovalStatus` of `openapi.yaml`. */
export type ApprovalStatus =
  'pending_manager' | 'pending_hr' | 'approved' | 'rejected' | 'withdrawn';

/** `AttendanceCorrectionCreateRequest`. `employee_id` is deliberately absent — §8. */
export interface AttendanceCorrectionCreateRequest {
  readonly date: string;
  readonly reason_code: ExplanationReasonCode;
  readonly reason_note: string;
  readonly requested_start_at: string;
  readonly requested_end_at: string;
}

/** `AttendanceCorrection` of `openapi.yaml`. */
export interface AttendanceCorrection {
  readonly id: string;
  readonly employee_id: string;
  readonly date: string;
  readonly status: ApprovalStatus;
  readonly created_at: string;
  readonly requested_minutes?: number;
  readonly requires_hr_approval?: boolean;
  readonly reason_code?: ExplanationReasonCode;
}

/* ─────────────────────────────────────────────────── aggregates (Task 36) ── */

/**
 * `TimelineBucket.completeness_status`. Three values, and the third is **not** a
 * zero: `missing` means the bucket was computed and nothing was observed, which
 * `openapi.yaml` says in as many words is not the claim that the employee did
 * nothing.
 */
export type BucketCompleteness = 'complete' | 'partial' | 'missing';

/** `AggregateTopEntry` — display only, never a filter. */
export interface AggregateTopEntry {
  readonly name: string;
  readonly seconds: number;
}

/** `TimelineBucket` of `openapi.yaml`, hour granularity. */
export interface TimelineBucket {
  readonly employee_id: string;
  readonly timezone: string;
  readonly online_seconds: number;
  readonly active_seconds: number;
  readonly idle_seconds: number;
  readonly completeness_status: BucketCompleteness;
  readonly bucket_start?: string;
  readonly session_date?: string;
  readonly hour_local?: number;
  /**
   * Seconds worked with no name recorded. Counted in `online` and `active` and in
   * **no** category column — `uncategorized` means "no rule matched", which is a
   * different fact (Task 36, D-30). The screen must render this as time that
   * counted; see `web/shapes/my-activity.md` §4.2.
   */
  readonly private_seconds?: number;
  readonly app_seconds?: number;
  readonly web_seconds?: number;
  readonly avg_intensity?: number | null;
  readonly first_activity_at?: string | null;
  readonly last_activity_at?: string | null;
  readonly top_apps?: readonly AggregateTopEntry[];
  readonly top_domains?: readonly AggregateTopEntry[];
  readonly segment_count?: number;
}

/** One `(employee, date)` pair with segments and no aggregate row. */
export interface NotComputedDay {
  readonly employee_id: string;
  readonly session_date: string;
}

/**
 * `AggregateCoverage` — "the difference between 'not computed' and 'nothing
 * happened'". A bucket that was not computed has no row and is never zeros.
 */
export interface AggregateCoverage {
  readonly from: string;
  readonly to: string;
  readonly timezone: string;
  readonly computed_buckets: number;
  readonly not_computed_days: readonly NotComputedDay[];
  readonly complete: boolean;
  readonly not_computed_truncated?: boolean;
}

export interface TimelineBucketListResponse extends CursorListEnvelope<TimelineBucket> {
  readonly source: 'aggregate' | 'segments';
  readonly granularity?: 'hour' | 'day' | null;
  readonly coverage?: AggregateCoverage | null;
}

/* ────────────────────────────────────────────── classification (Task 34) ── */

/** `ActivityCategory` — the fifteen of Master §9.1. A closed vocabulary. */
export const ACTIVITY_CATEGORIES = [
  'productive',
  'neutral',
  'unproductive',
  'communication',
  'meeting',
  'development',
  'design',
  'accounting',
  'sales',
  'marketing',
  'research',
  'training',
  'system',
  'blocked',
  'uncategorized',
] as const;

export type ActivityCategory = (typeof ACTIVITY_CATEGORIES)[number];

/** `ProductivityClass` — `null` is a value: eleven categories make no claim. */
export type ProductivityClass = 'productive' | 'neutral' | 'unproductive' | 'unclassified' | null;

export interface ApplicationCatalogEntry {
  readonly id: string;
  readonly match_type: 'exact' | 'prefix' | 'regex';
  readonly match_field: 'app_name' | 'process_name';
  readonly match_value: string;
  readonly category: ActivityCategory;
  readonly scope_type: 'company' | 'department' | 'job_title';
  readonly version: number;
  readonly display_name?: string | null;
  readonly productivity_class?: ProductivityClass;
}

export interface WebsiteCatalogEntry {
  readonly id: string;
  readonly match_type: 'exact' | 'prefix' | 'regex';
  readonly match_field: 'domain' | 'domain_suffix';
  readonly match_value: string;
  readonly category: ActivityCategory;
  readonly scope_type: 'company' | 'department' | 'job_title';
  readonly version: number;
  readonly productivity_class?: ProductivityClass;
  /** The company marked this domain private: its name is never collected. */
  readonly is_private?: boolean;
  readonly is_blacklisted?: boolean;
  /**
   * Null exactly when `scope_type` is `company`. Declared for WP-K5: the save key is
   * `(company, scope_type, scope_key, match_field, match_value, effective_from)`, so
   * a client that could not read this could not tell which existing rule a draft
   * would replace — and would render every edit as a brand-new rule.
   */
  readonly scope_key?: string | null;
}

/** `EffectiveCatalogResponse` — the rules that judge **this** employee on a day. */
export interface EffectiveCatalogResponse {
  readonly employee_id: string;
  readonly on_date: string;
  readonly applications: readonly ApplicationCatalogEntry[];
  readonly websites: readonly WebsiteCatalogEntry[];
  readonly department_id?: string | null;
  readonly job_title?: string | null;
}

/* ─────────────────────────────────────────────────── scoring (Tasks 38–39) ── */

/** `ScoreComponent` of `openapi.yaml`. `data_available: false` is not a zero. */
export interface ScoreComponent {
  readonly key: string;
  readonly raw_value: number | null;
  readonly normalised_value: number | null;
  readonly weight: number;
  readonly contribution: number;
  readonly label?: string;
  readonly data_available?: boolean;
}

/**
 * `ScoreExplanation`. `limitations` is `minItems: 1` in the contract and its
 * guaranteed member is the sentence that the score must not be the sole basis for
 * a disciplinary decision — so it is required here too, and rendered always.
 */
export interface ScoreExplanation {
  readonly employee_id: string;
  readonly period_start: string;
  readonly period_end: string;
  readonly score: number;
  readonly formula_version: number;
  readonly components: readonly ScoreComponent[];
  readonly limitations: readonly string[];
  /**
   * `ScoreFreshness` — G9 / D-37, `required` on every score.
   *
   * Typed `unknown` rather than as the interface, and deliberately: the one thing
   * this field must never do is read as "settled" when the server did not say so, and
   * a declared shape would let a caller destructure `state` from a body that carried
   * none. `readFreshness` in `web/src/scoring/freshness.ts` is the only reader, and it
   * fails closed on an absent, malformed or self-contradictory object.
   */
  readonly freshness?: unknown;
  readonly formula_id?: string;
  readonly positive_drivers?: readonly string[];
  readonly negative_drivers?: readonly string[];
  readonly data_completeness?: number;
  readonly confidence?: number;
  readonly missing_data_warnings?: readonly string[];
}

/* ───────────────────────────────────────────────── timers (Tasks 29–32) ── */

/** `TimerState` — six values, verbatim from the CHECK in migration 009. */
export type TimerState =
  'running' | 'paused' | 'stopped' | 'pending_approval' | 'approved' | 'rejected';

/**
 * `TaskValidationState`. Three facts kept apart (Task 31):
 * `pending_validation` = we could not ask, `validation_failed` = we asked and the
 * answer was no, `validated` = we asked and the answer was yes. Nothing is deleted
 * in any of the three.
 */
export type TaskValidationState = 'validated' | 'pending_validation' | 'validation_failed';

/** `TimeEntrySource` — `manual` is separated from agent-observed time everywhere. */
export type TimeEntrySource = 'agent' | 'web' | 'manual' | 'api';

/** `TimeEntry` of `openapi.yaml`. */
export interface TimeEntry {
  readonly id: string;
  readonly employee_id: string;
  readonly started_at: string;
  readonly status: TimerState;
  readonly source: TimeEntrySource;
  readonly task_validation_state: TaskValidationState;
  readonly achau_task_id?: string | null;
  readonly task_title?: string | null;
  readonly ended_at?: string | null;
  readonly duration_seconds?: number;
  readonly paused_seconds?: number;
  readonly paused_at?: string | null;
  readonly resumed_at?: string | null;
  readonly note?: string | null;
  readonly billable?: boolean;
  readonly approval_required?: boolean;
  readonly approved_at?: string | null;
  readonly rejected_reason?: string | null;
  readonly edit_requested_at?: string | null;
  readonly edit_request_note?: string | null;
  readonly auto_stopped?: boolean;
  readonly auto_stop_reason?: string | null;
  readonly previous_entry_id?: string | null;
  /** `achau_unreachable` beside `pending_validation`; one of three reasons beside a failure. */
  readonly task_validation_reason?: string | null;
  /** Null while nobody has been able to ask — never rendered as a blank. */
  readonly task_validated_at?: string | null;
}

/** `TimerStartResponse` — the new entry plus the one it displaced. */
export interface TimerStartResponse extends TimeEntry {
  readonly switched_from?: TimeEntry | null;
}

export type TimeEntryListResponse = CursorListEnvelope<TimeEntry>;

export interface TimerStartRequest {
  readonly achau_task_id: string;
  readonly switch_from_active?: boolean;
  readonly task_title?: string;
}

export interface ManualTimeEntryRequest {
  readonly achau_task_id: string;
  readonly started_at: string;
  readonly ended_at: string;
  readonly note: string;
  readonly task_title?: string;
}

/* ───────────────────────────────────────────────── task search (Task 30) ── */

export interface TaskSearchResultItem {
  readonly task_id: string;
  readonly title: string;
  readonly source: 'cache' | 'achau';
  /** Per row, not per response: one row can be minutes old and its neighbour an hour. */
  readonly stale: boolean;
  readonly status?: string | null;
  readonly project?: string | null;
  readonly closed?: boolean;
  readonly cached_at?: string | null;
  readonly due_date?: string | null;
}

/**
 * `TaskSearchResponse`. `degraded` and `cache_status` are two different questions
 * and are never merged — "achau is down" and "the sweep is late" need different
 * actions from different people.
 */
export interface TaskSearchResponse extends OffsetListEnvelope<TaskSearchResultItem> {
  readonly degraded: boolean;
  readonly cache_status: 'fresh' | 'stale' | 'never_synced';
}

/* ══════════════════ WP-K3 (Task 43) — the attendance grid, for other people ══
 *
 * The slice `/attendance` reads. It used to be **larger than `openapi.yaml`'s
 * `AttendanceDay`** — the divergence was the server's, and was recorded here, in
 * `docs/API_CONTRACT.md` §8.3 and in `renderAttendanceDay`'s own comment:
 *
 *   > "`openapi.yaml` … is **missing the fields this task's acceptance criterion is
 *   > about** — there is no status field on it at all, and every metric is
 *   > `type: integer, minimum: 0`, so the schema as written cannot express either
 *   > `data_incomplete` or an absent metric."
 *
 * QA-B-026 closed it: the schema now declares all twenty-six fields the route sends,
 * every metric as `integer | null`, and `employee_id` in the id space the server
 * actually uses. So these types and the document agree again, and
 * `tests/integration/attendance/calculation.test.ts` is what keeps them agreeing.
 * Two consequences are still load-bearing on this screen:
 *
 *  1. **A metric is `number | null`.** `docs/DATA_MODEL.md` D-22: the four
 *     shift-relative metric columns are `NOT NULL DEFAULT 0`, so a *withheld* value
 *     is indistinguishable from a measured zero in the table and only
 *     `attendance_status` and `withheld` tell them apart on the wire. A type of
 *     `number` here would have made the client's zero the only reading available.
 *  2. **`adjustments_applied` is not in this file.** `openapi.yaml` declared it and
 *     `API_CONTRACT.md` §8.3 described it, and the string appears **nowhere** in
 *     `src/` — `renderAttendanceDay` does not produce it. A field typed here that
 *     the server never sends is a branch that silently never runs. QA-B-026 removed
 *     it from both documents rather than leaving this file the lone dissenter; see
 *     `web/shapes/attendance.md` §4 item 4 for what the drawer shows instead.
 */

/** `AttendanceStatus` of `src/modules/attendance/calc.ts` — the eight wire values. */
export const ATTENDANCE_STATUSES = [
  'on_time',
  'late',
  'early_leave',
  'late_and_early',
  'absent',
  'off_day',
  'data_incomplete',
  'not_configured',
] as const;

export type AttendanceStatus = (typeof ATTENDANCE_STATUSES)[number];

/**
 * The ninth badge. `wp_work_sessions.attendance_status` permits `leave`, an approved
 * `reason_code = 'approved_leave'` writes it, and `calc.ts` — which is what the read
 * path re-runs on every request — **never emits it**. `web/shapes/attendance.md` §5.1
 * is the whole account. It is carried here so that the day the server does send it,
 * the badge exists rather than the screen falling through to an unknown value.
 */
export const ATTENDANCE_BADGE_STATUSES = [...ATTENDANCE_STATUSES, 'leave'] as const;

export type AttendanceBadgeStatus = (typeof ATTENDANCE_BADGE_STATUSES)[number];

/** `AttendanceReason` of `calc.ts` — why a metric is absent, or why a day has no verdict. */
export type AttendanceReason =
  | 'no_shift_configured'
  | 'not_a_working_day'
  | 'no_session_row'
  | 'agent_data_missing'
  | 'no_enrolled_device'
  | 'incomplete_data'
  | 'day_in_progress'
  | 'no_arrival_observed'
  | 'no_departure_observed'
  | 'overtime_requires_manual_claim'
  | 'overtime_not_credited_on_incomplete_data'
  | 'overtime_rule_unrecognised'
  | 'stale_source_version';

/** The four shift-relative metrics — `METRIC_NAMES` of `calc.ts`. The arguable ones. */
export const ATTENDANCE_METRIC_NAMES = [
  'late_minutes',
  'early_leave_minutes',
  'missing_minutes',
  'overtime_minutes',
] as const;

export type AttendanceMetricName = (typeof ATTENDANCE_METRIC_NAMES)[number];

/**
 * `WithheldMetrics` — a partial map from metric to the reason it is absent.
 *
 * The server's invariant is that a metric is `null` **iff** it has an entry here
 * (`assertExplained`). The client does not assume it: an unexplained `null` is still
 * an absence and is still not a zero.
 */
export type WithheldMetrics = Partial<Record<AttendanceMetricName, AttendanceReason>>;

/**
 * `completeness.status` — five values, and the fifth is not one of Task 26's four.
 *
 * `no_session_row` is what the service reports for a `(employee, date)` cell that has
 * no `wp_work_sessions` row at all: "calling it `missing` would claim a row exists
 * saying so".
 */
export type SessionCompleteness = 'complete' | 'partial' | 'missing' | 'no_data' | 'no_session_row';

/** The completeness block `GET /attendance` actually sends. Wider than `Completeness`. */
export interface AttendanceCompleteness {
  readonly status: SessionCompleteness;
  readonly score: number;
  readonly missing_agent_data: boolean;
  readonly unsynced_data: boolean;
  readonly data_gap_seconds: number;
  readonly has_clock_anomaly: boolean;
  readonly device_count: number;
  readonly event_count: number;
}

/** `shift.source` — the provenance block the drawer exists to show. */
export interface AttendanceSource {
  readonly session_row_present: boolean;
  readonly session_source_version: number | null;
  readonly current_source_version: number;
  /** The row was built by an algorithm version this image does not run. */
  readonly stale: boolean;
  readonly session_calculated_at: string | null;
  readonly session_timezone: string | null;
  readonly contributing_device_ids: readonly string[];
  readonly multi_device_policy: string | null;
  readonly last_event_received_at: string | null;
}

/** `ShiftResolution.outcome` — Task 25's tagged union, all four branches named. */
export type ShiftOutcome = 'assigned' | 'system_default' | 'off_duty' | 'unconfigured';

export interface ShiftVia {
  readonly assignment_id: string;
  readonly scope_type: string;
  readonly scope_key: string;
  readonly priority: number;
}

/** The `shift` block. Only `assigned` and `system_default` carry a window. */
export interface AttendanceShift {
  readonly outcome: ShiftOutcome;
  readonly via: ShiftVia | null;
  readonly source: AttendanceSource;
  readonly shift_id?: string | null;
  readonly shift_code?: string;
  readonly timezone?: string;
  readonly starts_at?: string;
  readonly ends_at?: string;
  readonly crosses_midnight?: boolean;
  readonly elapsed_minutes?: number;
  readonly grace_late_minutes?: number;
  readonly grace_early_leave_minutes?: number;
  readonly reason?: string;
}

/**
 * One cell of the `(employee × date)` grid, as `renderAttendanceDay` sends it.
 *
 * Every metric is `number | null` for the D-22 reason in this section's header.
 * `employee_name` is on the wire and is what makes a roster readable without a
 * lookup: `GET /employees` (Task T2-A) exists now, but nothing this grid renders
 * depends on it — the name already comes back on this row.
 */
export interface AttendanceGridDay {
  readonly employee_id: string;
  readonly employee_name: string;
  readonly date: string;
  readonly attendance_status: AttendanceStatus;
  readonly stored_attendance_status: string;
  readonly data_incomplete: boolean;
  readonly scoreable: boolean;
  /**
   * The shift has not ended yet, so nothing about the end of the day is knowable —
   * `early_leave_minutes`, `missing_minutes` and `overtime_minutes` are withheld and
   * `completeness.status` reads `partial`. Published as its own boolean by QA-B-026;
   * before that a client had to read `reason`, `withheld` and `completeness` together
   * and know which of them outranks the others.
   */
  readonly day_in_progress: boolean;
  readonly reason: AttendanceReason | null;
  readonly shift_id: string | null;
  readonly shift_expected_minutes: number | null;
  readonly break_minutes: number | null;
  readonly late_minutes: number | null;
  readonly early_leave_minutes: number | null;
  readonly missing_minutes: number | null;
  readonly overtime_minutes: number | null;
  readonly online_minutes: number | null;
  readonly active_minutes: number | null;
  readonly idle_minutes: number | null;
  readonly approved_offline_minutes: number | null;
  readonly credited_minutes: number | null;
  readonly first_activity_at: string | null;
  readonly last_activity_at: string | null;
  readonly withheld: WithheldMetrics;
  readonly completeness: AttendanceCompleteness;
  readonly shift: AttendanceShift;
  readonly company_id?: string;
}

/**
 * The envelope `GET /attendance` sends, which is **not** `OffsetListEnvelope`.
 *
 * `total`, `offset` and `limit` are at the top level rather than under `page`, and
 * there is a `range` beside them carrying the timezone the dates were resolved in.
 * Transcribed from the route, not from `openapi.yaml`, because the route is what
 * answers: a `page` object typed here would be `undefined` at runtime and any code
 * reading it would throw on a response the server considers correct.
 */
export interface AttendanceGridResponse {
  readonly total: number;
  readonly offset: number;
  readonly limit: number;
  readonly range: {
    readonly date_from: string;
    readonly date_to: string;
    readonly timezone: string;
  };
  readonly data: readonly AttendanceGridDay[];
}

/** `CorrectionDecisionRequest`. `note` is mandatory on **both** approve and reject. */
export interface CorrectionDecisionRequest {
  readonly note: string;
  /** Approve only. Lets an approver grant less than requested; never more. */
  readonly approved_adjustment_minutes?: number;
}

/**
 * `AttendanceCorrection`, in the fuller shape the decision endpoints return.
 *
 * `requires_hr_approval` travels beside `status` because `approved` is derived from
 * **two** storage states, so `manager_approved` is ambiguous without it — and it is
 * **snapshotted when the request was filed**, never re-read from the company's
 * current policy. This screen reflects that; it does not re-derive it.
 */
export interface CorrectionRecord {
  readonly id: string;
  readonly employee_id: string;
  readonly company_id?: string;
  readonly date: string;
  readonly reason_code: ExplanationReasonCode;
  readonly requested_minutes: number;
  readonly approved_minutes: number | null;
  readonly requested_start_at: string;
  readonly requested_end_at: string;
  readonly reason_note: string | null;
  readonly adjustment_target: string;
  readonly evidence_keys: readonly string[];
  readonly status: ApprovalStatus;
  readonly requires_hr_approval: boolean;
  readonly decided_by: string | null;
  readonly decided_at: string | null;
  readonly decision_note: string | null;
  readonly created_at: string;
}

/* ══════════════════ WP-K4 (Task 44) — the overview dashboards ═══════════════
 *
 * The slice `/overview` reads. Two things about this section are load-bearing and
 * are structural rather than conventional:
 *
 *  1. **`DailyAggregateRow` does not declare `top_apps` or `top_domains`.**
 *     `GET /activities/timeline` sends them at `granularity=day` — application
 *     names and domain names, per named employee — and spec 01 §12's acceptance
 *     criterion is that no raw sensitive data appears on the overview. A type that
 *     does not name a field is a field the screen cannot read without adding it
 *     back here, in a diff, under review. That is the point: the guarantee lives in
 *     the shape, not in everybody remembering.
 *
 *     It would undo three earlier decisions at once — Task 33 keeps URLs
 *     domain-only under Level 1 and proved it by scanning every column; Task 35
 *     records private activity as time that *was* worked with no name; Task 36
 *     counts that time in the totals and in **no** category column so a score
 *     cannot penalise a privacy right. `web/shapes/overview.md` §4.2.
 *
 *  2. **No category seconds are declared, because none are published.**
 *     `wp_daily_aggregates` has `productive_seconds`, `neutral_seconds`,
 *     `unproductive_seconds` and `uncategorized_seconds`; `readDailyRows` selects
 *     none of them and `readTimeline` maps none of them. So Master §14.1's
 *     "productive ratio" has no source, and this screen renders the active/online
 *     ratio under its own name instead — shape file §4.5.
 */

/**
 * One `(employee, session_date)` row of `GET /activities/timeline` at
 * `granularity=day`, transcribed from `read-service.ts`'s day branch.
 *
 * `completeness_status` is `BucketCompleteness`, and `missing` means **the day was
 * computed and nothing was observed** — which `openapi.yaml` says in as many words
 * is not the claim that the employee did nothing.
 */
export interface DailyAggregateRow {
  readonly employee_id: string;
  readonly session_date: string;
  readonly timezone: string;
  readonly online_seconds: number;
  readonly active_seconds: number;
  readonly idle_seconds: number;
  readonly completeness_status: BucketCompleteness;
  readonly private_seconds?: number;
  readonly active_hours_count?: number;
  readonly timer_seconds?: number;
  readonly timer_manual_seconds?: number;
  readonly first_activity_at?: string | null;
  readonly last_activity_at?: string | null;
  readonly source_version?: number;
}

/**
 * The envelope the day read answers with. `page.has_more` is what §8 of the shape
 * file turns into a narrowed window rather than a partial sum presented as a total.
 */
export interface DailyAggregateListResponse {
  readonly data: readonly DailyAggregateRow[];
  readonly page: CursorPage;
  readonly source: 'aggregate' | 'segments';
  readonly granularity?: 'hour' | 'day' | null;
  readonly coverage?: AggregateCoverage | null;
  readonly range?: { readonly from: string; readonly to: string };
}

/**
 * The slice of `Device` the liveness strip reads — and nothing else.
 *
 * `hostname`, `last_known_ip`, `os_*` and `agent_version` are real fields on the
 * wire and are deliberately absent here: a machine name and an IP address beside a
 * named employee is exactly the kind of row that belongs on the Devices screen
 * (WP-K5) behind `workforce.manage_devices`, not in an aggregate strip.
 *
 * `liveness` keeps `stale` apart from `offline` because Task 19 does: it is
 * computed from `last_heartbeat_at` on every read, never stored, and it lags
 * **pessimistically** — a device is called offline slightly before it truly is,
 * never slightly after.
 */
export interface DeviceSummary {
  readonly device_id: string;
  readonly liveness: DeviceLiveness;
  readonly status: string;
  readonly employee_id?: string | null;
  readonly last_heartbeat_at?: string | null;
}

export type DeviceListResponse = OffsetListEnvelope<DeviceSummary>;

/* ══════════════════ WP-K5 (Task 45) — devices, tokens and the policy surface ══
 *
 * Shaped from `renderDevice`, `renderToken` and `DeviceHealthBody` in
 * `src/modules/devices/`, and from `renderWebsiteEntry` in
 * `src/modules/activity/catalog-service.ts` — the functions that actually build the
 * bodies — for the reason the WP-K3 block above gives: a fixture that invents a
 * field is a test passing against a response the server cannot send.
 */

/**
 * `DeviceStatus` — the **lifecycle**, nine values, stored in `wp_devices.status`.
 *
 * A separate axis from `DeviceLiveness`, and never merged with it: `openapi.yaml`
 * says a device can answer `status: "active"` with `liveness: "offline"` in the same
 * body and that this "is the truth about both". `stale` is deliberately not a member
 * here — "it is not something a device *is*, it is something a clock says about it,
 * and there is no column for it".
 */
export const DEVICE_STATUSES = [
  'pending',
  'active',
  'offline',
  'suspended',
  'revoked',
  'uninstalled',
  'agent_outdated',
  'policy_error',
  'sync_error',
] as const;

export type DeviceStatus = (typeof DEVICE_STATUSES)[number];

/**
 * `Device` — the fleet row, as `renderDevice` builds it.
 *
 * `online` is on the wire and is **not declared here**, deliberately. It is
 * `liveness === "online"` and nothing more, so a screen that read it would be
 * folding three states into two — the exact collapse `PRODUCT.md` §4 and this
 * build's standing constraints forbid. Omitting it makes reading one a type error
 * before it is a defect, the same technique Task 44 used on `top_domains`.
 *
 * The **fingerprint** is absent for a different reason: `wp_devices` stores only
 * `fingerprint_hash` and no response returns it at all.
 */
export interface Device {
  readonly device_id: string;
  readonly company_id: string;
  readonly employee_id: string | null;
  readonly hostname: string | null;
  readonly os_name: string | null;
  readonly os_version: string | null;
  readonly os_architecture: string | null;
  readonly agent_version: string | null;
  readonly last_known_ip: string | null;
  readonly last_heartbeat_at: string | null;
  readonly liveness: DeviceLiveness;
  readonly status: DeviceStatus;
  readonly status_reason: string | null;
  readonly trust_status: string | null;
  readonly policy_id: string | null;
  readonly policy_version: string | null;
  readonly department_name: string | null;
  readonly branch: string | null;
  readonly registered_at: string;
  readonly approved_at: string | null;
  readonly approved_by: string | null;
  readonly revoked_at: string | null;
  readonly revocation_reason: string | null;
}

/** The flat `{ data, page: { total, offset, limit } }` envelope `listDevices` sends. */
export interface DeviceAdminListResponse {
  readonly data: readonly Device[];
  readonly page: OffsetPage;
}

/**
 * The four flag values this build actually emits.
 *
 * `queue_backpressure` and `credential_rotation_due` are in `openapi.yaml`'s enum
 * and are documented there as **never emitted** — "a flag that needs a threshold
 * nobody has configured would be a policy hard-coded in source". They are not
 * members here, so no screen can render a legend row for a condition that cannot
 * occur. `flags` on the wire is `readonly string[]`, so an unexpected value renders
 * verbatim rather than being dropped.
 */
export const DEVICE_HEALTH_FLAGS = [
  'clock_skew',
  'sequence_gap',
  'sync_error',
  'policy_error',
] as const;

export type DeviceHealthFlag = (typeof DEVICE_HEALTH_FLAGS)[number];

/**
 * `DeviceHealth` — computed, never read from a column.
 *
 * Two members carry `null` as a **value** rather than as a missing number, and both
 * matter on this screen: `offline_minutes` is null when the device is online *and*
 * when it has never reported ("there is no silence to measure from"), and
 * `pending_local_events` is null when the device has never reported, because "`0`
 * there would be a claim rather than an absence". `online` is omitted here for the
 * reason `Device` omits it.
 */
export interface DeviceHealth {
  readonly device_id: string;
  readonly status: DeviceStatus;
  readonly liveness: DeviceLiveness;
  readonly last_heartbeat_at: string | null;
  readonly offline_minutes: number | null;
  readonly agent_version: string | null;
  readonly agent_outdated: boolean;
  readonly agent_state: string | null;
  readonly policy_version: string | null;
  readonly policy_ack_at: string | null;
  readonly last_event_at: string | null;
  readonly last_device_sequence: number | null;
  readonly pending_local_events: number | null;
  readonly rejected_events_24h: number;
  readonly flags: readonly string[];
}

export interface DeviceApproveRequest {
  readonly employee_id?: string;
  readonly reason?: string;
}

/** `reason` is required and `minLength: 3` — two check constraints demand one. */
export interface DeviceRevokeRequest {
  readonly reason: string;
}

/**
 * `RegistrationToken` **minus the plaintext**, which is the whole point.
 *
 * `GET /devices/registration-tokens` has no `token` member on the wire — "the
 * plaintext is not here, and cannot be: the server stores a sha256 of it and there
 * is no field in this response for one" — and this type has none either, so a screen
 * that tried to read one off a list row would not compile. That is the structural
 * half of the render-once guarantee; `web/src/devices/reveal.ts` is the other half.
 *
 * `token_prefix` is eight characters "drawn from their own random source at mint
 * time and therefore not a prefix of the secret", which is why a row may show it.
 */
export interface RegistrationTokenSummary {
  readonly token_id: string;
  readonly token_prefix: string;
  readonly label: string | null;
  readonly expires_at: string;
  readonly max_uses: number;
  readonly used_count: number;
  readonly auto_approve: boolean;
  readonly default_employee_id: string | null;
  readonly last_used_at: string | null;
  readonly revoked_at: string | null;
  readonly revoke_reason: string | null;
  readonly created_at: string;
}

export interface RegistrationTokenListResponse {
  readonly data: readonly RegistrationTokenSummary[];
  readonly page: OffsetPage;
}

/**
 * The `201` from `POST /devices/registration-tokens`, and the only shape in this
 * file that carries a secret.
 *
 * `token` is required here — the creation response always has it — and
 * `enrollment_url` is optional because the server omits it when
 * `PULSE_PUBLIC_BASE_URL` is unset. The screen renders the absence as a named
 * configuration gap rather than as an empty field.
 */
export interface RegistrationTokenCreated extends RegistrationTokenSummary {
  readonly token: string;
  readonly enrollment_url?: string;
}

export interface RegistrationTokenCreateRequest {
  readonly expires_in_seconds?: number;
  readonly max_uses?: number;
  readonly employee_id?: string | null;
  readonly auto_approve?: boolean;
  readonly note?: string | null;
}

export interface RegistrationTokenRevokeRequest {
  readonly reason: string;
}

/* ─────────────────────────────────────── the writable half of /policies ── */

/** The flat envelope `GET /admin/activity-catalog/websites` sends. */
export interface WebsiteCatalogListResponse {
  readonly total: number;
  readonly offset: number;
  readonly limit: number;
  readonly data: readonly WebsiteCatalogEntry[];
}

/** `GET /admin/activity-catalog/categories` — the closed vocabulary, read not guessed. */
export interface CategoryVocabularyResponse {
  readonly data: readonly {
    readonly category: ActivityCategory;
    readonly productivity_class?: ProductivityClass;
  }[];
}

/** What `POST /admin/activity-catalog/websites` accepts. `strict` on the server. */
export interface WebsiteCatalogEntryRequest {
  readonly match_value: string;
  readonly category: ActivityCategory;
  readonly match_type?: 'exact' | 'prefix' | 'regex';
  readonly match_field?: 'domain' | 'domain_suffix';
  readonly is_blacklisted?: boolean;
  readonly is_private?: boolean;
  readonly scope_type?: 'company' | 'department' | 'job_title';
  readonly scope_key?: string | null;
}

/* ══════════════════ WP-L5 (Task 53) — alerts, incidents and data risk ═══════
 *
 * Shaped from `renderAlert` (`src/modules/alerting/alerts-service.ts`),
 * `renderIncident` and `renderComment` (`src/modules/incidents/view.ts`) and the two
 * routers' own envelopes — not from `openapi.yaml` alone — for the reason the WP-K3
 * block above gives: a fixture that invents a field is a test passing against a
 * response the server cannot send.
 *
 * Two divergences from the document are recorded here rather than reproduced:
 *
 *  - **`IncidentComment` carries four members and nothing else.** `view.ts`'s own
 *    docblock says the from/to pair "lives in `metadata_json` on the wire"; the
 *    function it documents emits `{id, author_id, body, created_at}` and no
 *    `metadata` of any kind. So a timeline can show when, who and what was written,
 *    and **cannot** show which transition a row records. Typed as what is sent.
 *  - **`Incident` has no `category`.** `auto-create.ts` classifies
 *    `bulk_file_operation` as `data_risk`, and nothing publishes it, so incidents
 *    cannot be narrowed to data-risk ones from a browser.
 */

/** `Severity` of `openapi.yaml` — five values, and `informational` is not `info`. */
export type AlertSeverity = 'informational' | 'low' | 'medium' | 'high' | 'critical';

/**
 * `Alert.status` — derived on the server, not a column.
 *
 * `resolved` is in the enum and **is not produced by this version**: nothing resolves
 * an alert, there is no `resolved_at`, and an acknowledged row cannot be updated
 * again. `?status=resolved` is accepted and matches nothing. Kept in the type because
 * the server accepts it; the filter does not offer it, because offering a value that
 * can only ever return an empty page is offering a wrong answer.
 */
export type AlertStatus = 'open' | 'acknowledged' | 'suppressed' | 'resolved';

/** `AlertTrigger` — Master §15.1, and `blocked_app` is spelled `app`. */
export type AlertTrigger =
  | 'extended_idle'
  | 'no_active_task'
  | 'estimate_exceeded'
  | 'productivity_drop'
  | 'workload_overload'
  | 'workload_underload'
  | 'late_arrival'
  | 'early_leave'
  | 'missing_hours'
  | 'agent_offline'
  | 'agent_stopped'
  | 'agent_uninstalled'
  | 'unknown_device'
  | 'usb_unapproved'
  | 'bulk_file_operation'
  | 'blocked_app'
  | 'blocked_website'
  | 'after_hours_activity';

/**
 * One `Alert`.
 *
 * `measured_value` and `threshold` are `number | null` — **not** `number | undefined`.
 * The difference is D-22: a withheld figure typed as optional invites `?? 0`, and a
 * `0` on a queue row is a measurement somebody acts on. `null` here means the server
 * published no number, and `readingOf` in `web/src/alerts/inbox.ts` turns it into a
 * union with no `.value` on the withheld branch, so the confusion cannot compile.
 *
 * `group_key: null` likewise means **withheld**, never absent: the column is NOT NULL
 * in the database, and the whole key is replaced — not trimmed — when a component
 * names an application or a domain this caller may not see. `redacted` is then true.
 */
export interface AlertSummary {
  readonly id: string;
  readonly rule_id: string;
  readonly company_id?: string;
  readonly trigger: AlertTrigger;
  readonly severity: AlertSeverity;
  readonly status: AlertStatus;
  readonly group_key: string | null;
  readonly employee_id: string | null;
  readonly device_id: string | null;
  readonly summary: string;
  readonly measured_value: number | null;
  readonly threshold: number | null;
  readonly occurrence_count: number;
  readonly detail: Readonly<Record<string, unknown>>;
  readonly redacted: boolean;
  readonly triggered_at: string;
  readonly acknowledged_at: string | null;
  readonly acknowledged_by: string | null;
  readonly incident_id: string | null;
}

/** `{ data, page: { next_cursor, has_more } }` — cursor, so **no total**. */
export interface AlertListResponse {
  readonly data: readonly AlertSummary[];
  readonly page: CursorPage;
}

/** `AlertAcknowledgeRequest` — a note and nothing else. */
export interface AlertAcknowledgeRequest {
  readonly note?: string | null;
}

/** `IncidentState` — Master §15.3's eight. The client state machine owns the table. */
export type IncidentStateName =
  | 'new'
  | 'acknowledged'
  | 'investigating'
  | 'waiting_for_employee'
  | 'resolved'
  | 'false_positive'
  | 'escalated'
  | 'closed';

/** One `evidence[]` entry. References only — the bytes are in object storage. */
export interface IncidentEvidence {
  readonly kind: 'activity_event' | 'time_entry' | 'attendance_day' | 'alert' | 'file' | 'note';
  readonly ref: string;
  readonly captured_at?: string | null;
}

/**
 * One `IncidentComment`, exactly as `renderComment` sends it.
 *
 * `author_id: null` means **the system wrote this row** — the opening of an
 * auto-created incident, and anything a later job records. The column is nullable
 * precisely so that "the system did this" is not attributed to whoever happened to be
 * on shift, so it renders as *hệ thống* and never as a blank.
 *
 * ── `from_state` / `to_state` — required, nullable, and never omitted (G19) ──
 *
 * `IncidentStateNullable` in `openapi.yaml`, written as a 3.1 `oneOf` rather than
 * `nullable:`, which is not a keyword in a 3.1 document (that was G15's defect). Both
 * members are in the schema's `required` list, so they are **declared non-optional
 * here** and a `null` is a value the server sent rather than a member it omitted.
 *
 * `null` means **this row records no state change** — a note, an evidence entry, the
 * `system` row that opens an incident. It does not mean unknown and it does not mean
 * "not published". `web/src/incidents/timeline.ts` is where the three readings are
 * kept apart, because the moment a component writes `?? ''` for one of these the
 * timeline starts showing a dash where a transition is and a dash where a note is,
 * and a reader cannot tell a quiet incident from a broken one.
 *
 * The pair was never missing from storage: `wp_incident_comments` has carried both
 * columns since Task 51 and a deferred CONSTRAINT TRIGGER refuses a status change
 * that does not write them. It was missing from the wire until `d3a0e59`.
 */
export interface IncidentComment {
  readonly id: string;
  readonly author_id: string | null;
  readonly body: string;
  readonly from_state: IncidentStateName | null;
  readonly to_state: IncidentStateName | null;
  readonly created_at: string;
}

/**
 * One `Incident`.
 *
 * `version` is declared because `openapi.yaml` declares it and is **not populated in
 * v1**; it is optional here so that reading it is visibly reading an absence. The
 * concurrency token is the `ETag` header, and `web/src/api/client.ts` is the only
 * place it is read from.
 *
 * `sla_breached` is derived as well as stamped, so it is the authority on whether the
 * deadline was missed — `web/src/incidents/sla.ts` explains why the client must not
 * decide that by comparing timestamps.
 */
export interface Incident {
  readonly id: string;
  readonly company_id: string;
  readonly incident_number: number;
  readonly title: string;
  readonly description: string | null;
  readonly state: IncidentStateName;
  readonly severity: AlertSeverity;
  readonly owner_id: string | null;
  readonly employee_id: string | null;
  readonly device_id: string | null;
  readonly alert_ids: readonly string[];
  readonly sla_due_at: string | null;
  readonly sla_breached: boolean;
  readonly evidence: readonly IncidentEvidence[];
  readonly comments?: readonly IncidentComment[];
  readonly redacted: boolean;
  readonly resolution: string | null;
  readonly achau_task_id: string | null;
  readonly created_at: string;
  readonly updated_at: string;
  readonly version?: number;
}

/** `{ data, page: { total, offset, limit } }` — offset, so the total is real. */
export interface IncidentListResponse {
  readonly data: readonly Incident[];
  readonly page: OffsetPage;
}

/**
 * `IncidentTransitionRequest`.
 *
 * `note` is required on **every** move and becomes the body of the history row —
 * a move with no note would be a step in an incident's life that nobody explained.
 * `resolution` is required when `to_state` is one of the three concluding states,
 * which the form demands before sending rather than learning from a `400`.
 */
export interface IncidentTransitionRequest {
  readonly to_state: IncidentStateName;
  readonly note: string;
  readonly resolution?: string | null;
}

/** `IncidentPatchRequest` — metadata only. `state` is refused, not ignored. */
export interface IncidentPatchRequest {
  readonly title?: string;
  readonly description?: string | null;
  readonly severity?: AlertSeverity;
  readonly owner_id?: string | null;
  readonly sla_due_at?: string | null;
}

/** One `(domain × classification)` row of `GET /activities/websites`. */
export interface WebsiteUsageRow {
  readonly domain: string | null;
  readonly category: ActivityCategory;
  readonly productivity_class?: ProductivityClass;
  readonly classification_source: string;
  readonly total_seconds: number;
  readonly active_seconds?: number;
  readonly visit_count: number;
  readonly employee_count?: number;
  readonly collection_level?: 1 | 2 | 3;
  readonly blacklisted?: boolean;
  readonly private_activity?: boolean;
}

/** The flat envelope `GET /activities/websites` sends. */
export interface WebsiteUsageListResponse {
  readonly total: number;
  readonly offset: number;
  readonly limit: number;
  readonly range: {
    readonly date_from: string;
    readonly date_to: string;
    readonly timezone?: string;
  };
  readonly catalog_as_of: string;
  readonly data: readonly WebsiteUsageRow[];
}

/* ══════════════════ WP-K8 (Task 48) — reports, schedules and the audit log ══
 *
 * Transcribed from `openapi.yaml` and from nothing else. Every member below is one
 * the server actually sends: a type carrying a field the contract does not publish
 * is a fixture waiting to be written, and a test passing against a response that
 * cannot arrive.
 */

/**
 * `ReportType` — the thirteen values `wp_reports.report_type` accepts.
 *
 * **Five have a builder in MVP.** The other eight are refused at `POST /reports`
 * rather than accepted and generated empty, so the create form must say which is
 * which: offering thirteen options of which eight answer `400` is a form that
 * teaches people to distrust it.
 */
export type ReportTypeName =
  | 'attendance'
  | 'shift_compliance'
  | 'active_idle'
  | 'app_usage'
  | 'website_usage'
  | 'task_timer'
  | 'estimate_vs_actual'
  | 'productivity'
  | 'workload'
  | 'usb_file_events'
  | 'alerts_incidents'
  | 'device_health'
  | 'audit_log';

/** `ReportFormat` — exactly what `wp_reports.format`'s CHECK accepts. `json` is not one. */
export type ReportFormatName = 'csv' | 'xlsx' | 'pdf';

/**
 * `ReportStatus` — exactly what `wp_reports.status` holds.
 *
 * Five states and none is a synonym of another: `pending` has not started,
 * `running` is in a worker, `done` has a file, `failed` produced none, and
 * `expired` **had** one that retention removed. The last two are the pair a screen
 * must never merge — "we could not make your file" and "your file was made and has
 * since been deleted" send the person to two different places.
 */
export type ReportStatusName = 'pending' | 'running' | 'done' | 'failed' | 'expired';

/**
 * `Report.scope_snapshot` — the scope frozen at request time.
 *
 * `employee_count: null` means the scope was **the whole company**: an open set that
 * grows with every hire, which no count describes. It is emphatically not zero, and
 * `web/src/reports/figure.ts` is what stops it being rendered as one.
 */
export interface ReportScopeSnapshot {
  readonly level?: ScopeLevel;
  readonly employee_count?: number | null;
}

/**
 * One `Report`.
 *
 * `row_count` and `size_bytes` are `number | null` — **not** optional numbers. The
 * difference is D-22 again: an optional number invites `?? 0` and a `0` beside a
 * finished export reads as "your file is empty", when the truth is that the file
 * does not exist yet. `web/src/reports/figure.ts` turns each into a union whose
 * absent branch has no numeric member at all.
 */
export interface Report {
  readonly id: string;
  readonly company_id?: string;
  readonly report_type: ReportTypeName;
  readonly format: ReportFormatName;
  readonly status: ReportStatusName;
  readonly period_start?: string;
  readonly period_end?: string;
  readonly row_count?: number | null;
  readonly size_bytes?: number | null;
  readonly requested_by?: string;
  readonly requested_at: string;
  readonly completed_at?: string | null;
  readonly expires_at?: string | null;
  readonly failure_code?: string | null;
  readonly checksum_sha256?: string | null;
  readonly download_count?: number;
  readonly scope_snapshot?: ReportScopeSnapshot;
}

/**
 * `ReportListResponse` — `{ data, page: { total, offset, limit } }`.
 *
 * The `total` is a real count and not the page length, which is what lets the screen
 * say "showing 50 of 214" rather than implying the company has exported fifty things.
 */
export interface ReportListResponse {
  readonly data: readonly Report[];
  readonly page: OffsetPage;
}

/**
 * `ReportCreateRequest`. `additionalProperties: false`, four required members.
 *
 * An absent filter is an **omitted member**, never `null` and never `[]`: an empty
 * `employee_ids` is a different request from no `employee_ids`, and only the second
 * means "everybody in the caller's resolved scope". `ExportSlot`'s
 * `exportRequestBody` made the same decision and for the same reason.
 */
export interface ReportCreateRequest {
  readonly report_type: ReportTypeName;
  readonly format: ReportFormatName;
  readonly period_start: string;
  readonly period_end: string;
  readonly employee_ids?: readonly string[];
  readonly department_id?: string | null;
  readonly filters?: Readonly<Record<string, unknown>>;
  readonly timezone?: string | null;
}

/**
 * The body of `GET /reports/{id}/download?mode=json`.
 *
 * **This shape is handled and never stored.** `download_url` is a bearer credential
 * S3 does not re-check, alive for `WP_REPORT_URL_TTL` = 180 s (SEC-48; AR-7 was
 * retracted on that number). `web/src/reports/download.ts` hands it to one caller
 * that navigates immediately and keeps a state object with **no url member**, so a
 * component cannot render a stale link because there is no link in the model.
 */
export interface ReportDownloadGrant {
  readonly download_url: string;
  readonly expires_at: string;
  readonly filename?: string;
}

/** `frequency` of `wp_report_schedules`. There is no "last day of month". */
export type ScheduleFrequency = 'daily' | 'weekly' | 'monthly';

/** `period` — which days a run reports on. Every one of them ends **yesterday**. */
export type SchedulePeriod =
  'previous_day' | 'previous_week' | 'previous_month' | 'rolling_7d' | 'rolling_30d';

/**
 * `ReportScheduleCreateRequest`.
 *
 * `recipients` are **`wp_employees.id` of this company**, never free-form email: the
 * only way an id becomes a mailbox is a `SELECT … WHERE company_id = $1 AND id =
 * ANY($2) AND is_active`, so "outside the company" is a query returning no rows
 * rather than a rule somebody remembered to write.
 */
export interface ReportScheduleCreateRequest {
  readonly name: string;
  readonly report_type: ReportTypeName;
  readonly formats: readonly ReportFormatName[];
  readonly frequency: ScheduleFrequency;
  readonly day_of_week?: number | null;
  readonly day_of_month?: number | null;
  readonly hour_of_day?: number;
  readonly minute_of_hour?: number;
  readonly timezone?: string | null;
  readonly period?: SchedulePeriod;
  readonly recipients: readonly string[];
  readonly filters?: Readonly<Record<string, unknown>>;
  readonly enabled?: boolean;
}

/**
 * One `ReportSchedule`, as `POST /report-schedules` answers it.
 *
 * `last_status` is `success | failed | skipped | null` and the null is **two facts**:
 * with `last_run_at` also null the schedule has never run, and with `last_run_at` set
 * the occurrence is **in flight** — the outcome columns are cleared at enqueue
 * precisely so a retry does not still show last week's success.
 *
 * `last_error` is deliberately not published, so it is not declared here.
 */
export interface ReportSchedule {
  readonly id: string;
  readonly company_id: string;
  readonly name: string;
  readonly report_type: ReportTypeName;
  readonly formats: readonly ReportFormatName[];
  readonly frequency: ScheduleFrequency;
  readonly day_of_week?: number | null;
  readonly day_of_month?: number | null;
  readonly hour_of_day?: number;
  readonly minute_of_hour?: number;
  readonly timezone?: string;
  readonly period?: string;
  readonly recipients?: readonly string[];
  readonly enabled: boolean;
  readonly next_run_at: string;
  readonly last_run_at?: string | null;
  readonly last_status?: 'success' | 'failed' | 'skipped' | null;
  readonly consecutive_failures?: number;
  readonly created_at?: string;
}

/**
 * `ReportSchedulePatchRequest` — every field optional, `minProperties: 1`.
 *
 * Only the members this screen actually sends are declared, and that is deliberate
 * rather than lazy: the schema is `additionalProperties: false`, so a member declared
 * here and never sent is a member nobody checked against the server. Today the screen
 * sends exactly one — `enabled` — because pausing and resuming a standing export is
 * the lifecycle operation the Reports screen owns; re-editing the hour, the formats
 * or the recipients is the create form's shape and would be a second form.
 *
 * **`enabled: false` is a pause, not an end.** The row stays, `next_run_at` is kept,
 * the recipients are kept, and it can be switched back on. `DELETE` is the other act
 * and they are not collapsed — see `ScheduleManager`.
 */
export interface ReportSchedulePatchRequest {
  readonly enabled?: boolean;
}

/**
 * `AuditLogType` — which table a row came from.
 *
 * `audit` is `wp_audit_log`, administrative actions. `data_access` is
 * `wp_data_access_logs`, reads of sensitive data. They are kept apart on the wire
 * and are kept apart on screen, because "somebody changed a policy" and "somebody
 * looked at a person's day" are different events with different remedies.
 */
export type AuditLogTypeName = 'audit' | 'data_access';

/**
 * One `AuditLogEntry`.
 *
 * `reason` is `string | null` on **both** kinds of row and the null means something
 * different on each — see `web/src/audit/reason.ts`, which turns the pair into four
 * named facts rather than one blank cell.
 *
 * `before` / `after` are **metadata only** (G12): never a secret, never private
 * content. The detail panel says so, because an auditor who does not know it reads a
 * sparse `after` as evidence that a field was cleared.
 */
export interface AuditLogEntry {
  readonly id: string;
  readonly log_type: AuditLogTypeName;
  readonly actor_id: string;
  readonly actor_username?: string | null;
  readonly action: string;
  readonly resource_type?: string | null;
  readonly resource_id?: string | null;
  readonly company_id?: string;
  readonly occurred_at: string;
  readonly success: boolean;
  readonly reason?: string | null;
  readonly ip_address?: string | null;
  readonly user_agent?: string | null;
  readonly correlation_id?: string | null;
  readonly achau_run_id?: string | null;
  readonly before?: Readonly<Record<string, unknown>> | null;
  readonly after?: Readonly<Record<string, unknown>> | null;
}

/** `{ data, page: { next_cursor, has_more } }` — cursor, so **no total**. */
export interface AuditLogListResponse {
  readonly data: readonly AuditLogEntry[];
  readonly page: CursorPage;
}

/* ══════════════════ WP-M4 (Task 57) — the AI surface ════════════════════════
 *
 * Four endpoints, and only one of them touches a model. That table is
 * `API_CONTRACT.md` §8.9's own, and it is why "LiteLLM down → the dashboard still
 * runs" is a property of the architecture rather than of a `catch` block:
 *
 *   `GET  /ai/insights`               no model call   → `200`, the stored rows
 *   `POST /ai/analyze`                no (a job)      → `202 AcceptedJob`
 *   `POST /ai/chat`                   **yes**         → `200` + fallback + flag
 *   `GET  /ai/insights/{id}/evidence` no model call   → `200`
 */

/** `AiInsightType` — spec 18.2's list, one value per permitted analysis. */
export type AiInsightTypeName =
  | 'summary'
  | 'trend_comparison'
  | 'anomaly'
  | 'cause_hypothesis'
  | 'recommendation'
  | 'workload_analysis'
  | 'scoped_query';

/**
 * `wp_ai_insights.status`, verbatim — **the first of the four independent places a
 * reader can tell a fallback from a real insight**, and the one this screen switches
 * on.
 *
 * `failed` is deliberately not `ai_unavailable`: today its only cause is
 * `WORKFORCE_QUOTA_EXCEEDED`, and billing is not availability.
 */
export type AiInsightStatusName = 'published' | 'failed' | 'fallback' | 'superseded';

/** `AiEvidenceRef` — a pointer from a claim back to the aggregate that supports it. */
export interface AiEvidenceRefWire {
  readonly evidence_id: string;
  readonly kind: string;
  readonly ref: string;
  readonly period_start?: string | null;
  readonly period_end?: string | null;
}

/**
 * `AiInsight`.
 *
 * `limitations` is `minItems: 1` on the wire and is typed here as a plain array on
 * purpose: the non-empty guarantee is re-established by `web/src/ai/insight.ts` as a
 * **tuple**, because a client that trusted the server's `minItems` for the one field
 * whose absence is the defect would be trusting exactly the wrong promise.
 */
export interface AiInsight {
  readonly id: string;
  readonly insight_type: AiInsightTypeName;
  readonly scope?: {
    readonly level?: string;
    readonly employee_ids?: readonly string[];
    readonly department_id?: string | null;
  };
  readonly period_start?: string;
  readonly period_end?: string;
  readonly summary: string;
  readonly possible_causes?: readonly string[];
  readonly recommended_actions?: readonly string[];
  readonly confidence: number;
  readonly limitations: readonly string[];
  readonly evidence: readonly AiEvidenceRefWire[];
  readonly model: string;
  readonly model_version?: string | null;
  readonly generated_at: string;
  readonly question?: string | null;
  readonly status: AiInsightStatusName;
  readonly fallback_reason?: string | null;
  /** `status === 'fallback'`, and nothing else. Read only as a cross-check — see `ai/insight.ts`. */
  readonly ai_unavailable: boolean;
  readonly job_id?: string | null;
}

/** `{ data, page: { next_cursor, has_more } }` — cursor, so **no total**. */
export interface AiInsightListResponse {
  readonly data: readonly AiInsight[];
  readonly page: CursorPage;
}

/** `AiAnalyzeRequest`. An absent filter is an **omitted member**, never `null` or `[]`. */
export interface AiAnalyzeRequest {
  readonly insight_type: AiInsightTypeName;
  readonly period_start: string;
  readonly period_end: string;
  readonly employee_ids?: readonly string[];
  readonly department_id?: string | null;
  readonly question?: string | null;
}

/** `AiChatRequest`. `conversation_id` is **server-minted**; a made-up one is a `400`. */
export interface AiChatRequest {
  readonly message: string;
  readonly conversation_id?: string | null;
  readonly history?: readonly { readonly role: 'user' | 'assistant'; readonly content: string }[];
  readonly period_start?: string | null;
  readonly period_end?: string | null;
}

/**
 * `AiChatResponse`. **This shape answers `200` whether or not the model was
 * reachable** — with LiteLLM down the answer is the statistical fallback over the
 * same retrieved context. A budget refusal is *not* this shape: it is
 * `429 WORKFORCE_QUOTA_EXCEEDED` carrying the real numbers.
 */
export interface AiChatResponse {
  readonly conversation_id: string;
  readonly insight_id?: string;
  readonly answer: string;
  readonly status: AiInsightStatusName;
  readonly fallback_reason?: string | null;
  readonly ai_unavailable: boolean;
  readonly possible_causes: readonly string[];
  readonly recommended_actions: readonly string[];
  readonly evidence: readonly AiEvidenceRefWire[];
  readonly confidence: number;
  readonly limitations: readonly string[];
  readonly model: string;
  readonly refused?: boolean;
}

/**
 * `AiEvidenceResponse`.
 *
 * `value` is **absent** — not `null` — on an item the caller may not read. A key
 * carrying `null` is read by a renderer as "measured, and the result was nothing",
 * which is the D-22 shape one surface over. `web/src/ai/evidence.ts` keeps the
 * distinction in the type.
 */
export interface AiEvidenceItem {
  readonly evidence_id: string;
  readonly kind: string;
  readonly ref: string;
  readonly accessible: boolean;
  readonly value?: Readonly<Record<string, unknown>>;
}

export interface AiEvidenceResponse {
  readonly insight_id: string;
  readonly evidence: readonly AiEvidenceItem[];
}

/** `AcceptedJob` — what `POST /ai/analyze` returns. The poll is by `job_id`. */
export interface AcceptedJob {
  readonly job_id: string;
  readonly status: string;
}
